import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FileInput,
  ConfigurationContext,
} from '@kirz/mui-admin';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export const TemplateImagesTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      templateId: number;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { templateId, filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const [sortValues, setSortValues] = useState<number[] | null>(null);
    const { hasura } = useContext(ConfigurationContext);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const refreshSortValues = async () => {
      const items = await hasura.request({
        type: 'query',
        source: 'templateImage',
        selection: 'sort',
        where: { templateId: { _eq: templateId } },
      });

      setSortValues(items.map((x: any) => x.sort));
    };

    useEffect(() => {
      refreshSortValues();
    }, [templateId]);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            width: 60,
            field: 'sort',
            type: 'sort',
            sortKey: 'sort',
            rowsSortValues: sortValues,
            onSortChange: async (direction, row, targetRowSort) => {
              const source = 'TemplateImage';
              const { id } = row;

              await hasura.request({
                type: 'custom',
                query: `
                  mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
                    result2: update${source}(where: $where2, _set: $set2) {
                      affected_rows
                    }
                    result1: update${source}(where: $where1, _set: $set1) {
                      affected_rows
                    }
                  }
                `,
                variables: {
                  where1: { id: { _eq: id }, templateId: { _eq: templateId } },
                  set1: { sort: targetRowSort },
                  where2: {
                    sort: { _eq: targetRowSort },
                    templateId: { _eq: templateId },
                  },
                  set2: { sort: row.sort },
                },
              });

              tableRef.current?.reload();
            },
          },
          //   {
          //     field: 'id',
          //     headerName: 'ID',
          //     type: 'number',
          //     selector: 'sort',
          //     valueGetter({ row }) {
          //       return row.sort;
          //     },
          //   },
          {
            field: 'fileId',
            headerName: 'Изображение',
            type: 'file',
            hideText: true,
            selector: 'image { id contentType name extension }',
            width: 100,
            sortable: false,
            valueGetter({ row }) {
              return row.image?.id;
            },
            fetchMetadata(row) {
              if (!row.image) {
                return null;
              }

              return {
                ...row.image,
                thumbnail: `/api/v1/files/w_100,h_100/${row.image.id}`,
              };
            },
          },
          {
            field: '',
            headerName: '',
            flex: 1,
            selector: false,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell({ row }) {
              return null;
            },
          },
        ]),
      [filterColumns, sortValues],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="templateImage"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'sort', sort: 'asc' }}
        selectProps={{
          filter: {
            templateId: { _eq: templateId },
            ...props.selectProps?.filter,
          },
        }}
        formTitle={(isNew) =>
          isNew ? 'Новое изображение' : 'Редактировать изображение'
        }
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'xs',
          formSubmitterProps: {
            preSubmit(item) {
              return {
                ...item,
                templateId,
              };
            },
            onSubmit(item) {
              refreshSortValues();
            },
          },
        }}
        deletable={{
          onDeleted: (row) => {
            setSortValues(sortValues!.filter((x) => x !== row.sort));
          },
        }}
      >
        <FileInput
          name="fileId"
          label="Изображение"
          required
          dropzoneProps={{
            accept: {
              'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
            },
            validator(file) {
              if (
                file.type !== 'image/jpeg' &&
                file.type !== 'image/png' &&
                file.type !== 'image/heic' &&
                file.type !== 'image/heif'
              ) {
                return {
                  code: 'file-invalid-type',
                  message: 'Поддерживаемые форматы: JPG, JPEG, PNG, HEIC, HEIF',
                };
              }

              return null;
            },
          }}
        />
      </DataTableEx>
    );
  },
);
